import { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback, memo } from "react";
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    TableSortLabel, 
    Paper, 
    Checkbox, 
    TextField, 
    InputAdornment,
    Select,
    MenuItem,
    Button,
    IconButton,
    Autocomplete,
    Box,
    Tooltip,
    Popper,
    ClickAwayListener,
    Tabs,
    Tab
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    KeyboardSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import { downloadFile } from "../../api/fileDownloader";
import { moveKeyInList, saveColumnKeys, TABLE_COLUMN_TABLE_IDS } from "../../utils/tableColumnPreferences";
import SortableHeaderCell from "./SortableHeaderCell";
import styles from "./CustomTable.module.css";

/** Min 100, default 500, max 1000 — used wherever `showEntriesOptions` is not overridden. */
const DEFAULT_SHOW_ENTRIES_OPTIONS = Object.freeze([100, 500, 1000]);

function stripNumberGrouping(s) {
    return String(s).replace(/[\s,]/g, "");
}

function cellContainsTerm(cellStr, term) {
    const t = String(term ?? "").trim().toLowerCase();
    if (!t) return true;
    if (cellStr.includes(t)) return true;
    const cellCompact = stripNumberGrouping(cellStr);
    const searchCompact = stripNumberGrouping(t);
    return (
        searchCompact.length > 0 &&
        cellCompact.length >= searchCompact.length &&
        cellCompact.includes(searchCompact)
    );
}

/** Space-separated tokens all must match (order-independent AND). Quoted / AND / OR / NOT / wildcards kept. */
function textMatchesSearch(cellValue, searchTerm) {
    if (cellValue == null || cellValue === "") return false;

    const cellStr = String(cellValue).toLowerCase();
    const search = String(searchTerm ?? "").trim();
    const searchLen = search.length;
    if (searchLen === 0) return true;

    const searchLower = search.toLowerCase();

    if (searchLen > 2 && search[0] === '"' && search[searchLen - 1] === '"') {
        return cellStr === search.slice(1, -1).toLowerCase();
    }

    if (search.includes("*")) {
        try {
            const regexPattern = search
                .replace(/\*/g, ".*")
                .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                .replace(/\\\*/g, ".*");
            return new RegExp(`^${regexPattern}$`, "i").test(cellStr);
        } catch {
            return cellStr.includes(search.replace(/\*/g, "").toLowerCase());
        }
    }

    if (searchLower.includes(" and ")) {
        return search.split(/\s+and\s+/i).every((term) => cellContainsTerm(cellStr, term));
    }

    if (searchLower.includes(" or ")) {
        return search.split(/\s+or\s+/i).some((term) => cellContainsTerm(cellStr, term));
    }

    if (searchLower.includes(" not ")) {
        const parts = search.split(/\s+not\s+/i);
        if (parts.length === 2) {
            return cellContainsTerm(cellStr, parts[0]) && !cellContainsTerm(cellStr, parts[1]);
        }
    }

    const tokens = searchLower.split(/\s+/).filter(Boolean);
    return tokens.every((term) => cellContainsTerm(cellStr, term));
}

function collectRowSearchValues(row, columns) {
    const values = [];
    const seen = new Set();
    const push = (v) => {
        if (v == null || v === "") return;
        if (typeof v === "object") return;
        const s = String(v);
        if (seen.has(s)) return;
        seen.add(s);
        values.push(s);
    };
    if (Array.isArray(columns)) {
        columns.forEach((col) => {
            if (col?.key) push(row?.[col.key]);
        });
    }
    if (row && typeof row === "object") {
        Object.keys(row).forEach((key) => {
            const v = row[key];
            if (v != null && typeof v !== "object") push(v);
        });
    }
    return values;
}

function anyRowValueContainsTerm(values, term) {
    return values.some((v) => cellContainsTerm(String(v).toLowerCase(), term));
}

/** Each space-separated term can match a different column. All terms must match. */
function rowMatchesMultiTermSearch(row, columns, searchTerm) {
    const search = String(searchTerm ?? "").trim();
    if (!search) return true;
    const values = collectRowSearchValues(row, columns);
    if (values.length === 0) return false;

    const searchLower = search.toLowerCase();

    if (search.length > 2 && search[0] === '"' && search[search.length - 1] === '"') {
        const exact = search.slice(1, -1).toLowerCase();
        return values.some((v) => String(v).toLowerCase() === exact);
    }

    if (search.includes("*")) {
        return values.some((v) => textMatchesSearch(v, search));
    }

    if (searchLower.includes(" and ")) {
        return search.split(/\s+and\s+/i).every((term) => anyRowValueContainsTerm(values, term));
    }

    if (searchLower.includes(" or ")) {
        return search.split(/\s+or\s+/i).some((term) => anyRowValueContainsTerm(values, term));
    }

    if (searchLower.includes(" not ")) {
        const parts = search.split(/\s+not\s+/i);
        if (parts.length === 2) {
            return anyRowValueContainsTerm(values, parts[0]) && !anyRowValueContainsTerm(values, parts[1]);
        }
    }

    const tokens = searchLower.split(/\s+/).filter(Boolean);
    return tokens.every((term) => anyRowValueContainsTerm(values, term));
}

function highlightSearchTokens(text, searchTerm) {
    if (!searchTerm || text == null) return text;
    if (typeof text === "object" && text !== null && !Array.isArray(text)) {
        return text;
    }

    const textStr = String(text);
    const search = String(searchTerm).trim();
    if (!search) return textStr;

    if (search.length > 2 && search[0] === '"' && search[search.length - 1] === '"') {
        const exact = search.slice(1, -1);
        if (!exact) return textStr;
        const tokens = [exact];
        return highlightTokenParts(textStr, tokens);
    }

    if (/\s+(and|or|not)\s+/i.test(search)) {
        const tokens = search.split(/\s+(?:and|or|not)\s+/i).map((t) => t.trim()).filter(Boolean);
        return highlightTokenParts(textStr, tokens);
    }

    const tokens = search.split(/\s+/).filter(Boolean);
    return highlightTokenParts(textStr, tokens);
}

function highlightTokenParts(textStr, tokens) {
    const unique = [...new Set(tokens.map((t) => t.trim()).filter(Boolean))];
    if (unique.length === 0) return textStr;

    const escaped = unique
        .slice()
        .sort((a, b) => b.length - a.length)
        .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (escaped.length === 0) return textStr;

    let parts;
    try {
        parts = textStr.split(new RegExp(`(${escaped.join("|")})`, "gi"));
    } catch {
        return textStr;
    }

    const lowerSet = new Set(unique.map((t) => t.toLowerCase()));
    return parts.map((part, i) =>
        lowerSet.has(part.toLowerCase()) ? (
            <mark key={i} style={{ backgroundColor: "var(--highlight-search)", padding: "0 2px" }}>
                {part}
            </mark>
        ) : (
            part
        )
    );
}

function parseNumericCellValue(value) {
    if (typeof value === "number") return value;
    const n = Number(String(value ?? "").replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : NaN;
}

/** Position Book: buy side green, sell side red, other qty columns by sign. */
function positionBookValueTone(colKey, value) {
    const key = String(colKey ?? "").toLowerCase();
    if (!key) return null;
    if (key === "buy_sell" || key === "transaction_type" || key === "transaction" || key === "side") {
        return null;
    }

    const isBuyCol = key.includes("buy");
    const isSellCol = key.includes("sell");
    const isQtyCol =
        key === "qty" ||
        key === "quantity" ||
        key.includes("qty") ||
        key.includes("quantity");
    const isPriceCol = key.includes("price") || key.includes("ltp") || key.includes("rate");

    if (isBuyCol && (isQtyCol || isPriceCol || key === "buy" || key.startsWith("buy_"))) {
        return "positive";
    }
    if (isSellCol && (isQtyCol || isPriceCol || key === "sell" || key.startsWith("sell_"))) {
        return "negative";
    }
    if (!isQtyCol) return null;

    const numeric = parseNumericCellValue(value);
    if (!Number.isFinite(numeric) || numeric === 0) return null;
    return numeric < 0 ? "negative" : "positive";
}

function resolveInitialEntriesPerPage(requested, options) {
    const list = Array.isArray(options) && options.length > 0 ? options : DEFAULT_SHOW_ENTRIES_OPTIONS;
    const n = Number(requested);
    if (list.includes(n)) return n;
    return list.includes(500) ? 500 : list[Math.floor(list.length / 2)];
}

// Memoized dropdown list component for performance
const DropdownList = memo(({ columnKey, allValues, searchText, selectedValues, onToggle }) => {
    const filteredValues = useMemo(() => {
        if (!searchText) return allValues;
        return allValues.filter((v) => textMatchesSearch(v, searchText));
    }, [allValues, searchText]);

    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    const handleItemClick = useCallback((value, e) => {
        // Don't toggle if clicking directly on checkbox (it handles its own change)
        if (e.target.type === 'checkbox' || e.target.closest('input[type="checkbox"]') || e.target.closest('span[role="checkbox"]')) {
            return;
        }
        onToggle(columnKey, value);
    }, [columnKey, onToggle]);

    const handleCheckboxChange = useCallback((e, value) => {
        e.stopPropagation();
        onToggle(columnKey, value);
    }, [columnKey, onToggle]);

    if (filteredValues.length === 0) {
        return <div className={styles["dropdown-empty"]}>No matching values</div>;
    }

    return (
        <>
            {filteredValues.map((value) => {
                const isChecked = selectedSet.has(value);
                return (
                    <div
                        key={value}
                        className={styles["dropdown-item"]}
                        onClick={(e) => handleItemClick(value, e)}
                    >
                        <Checkbox
                            checked={isChecked}
                            size="small"
                            className={styles["dropdown-checkbox"]}
                            onChange={(e) => handleCheckboxChange(e, value)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                '&.Mui-checked': {
                                    color: 'rgba(79, 70, 229, 0.60)',
                                },
                                '&.Mui-checked .MuiSvgIcon-root': {
                                    fill: 'rgba(79, 70, 229, 0.60)',
                                    color: 'rgba(79, 70, 229, 0.60)',
                                },
                            }}
                        />
                        <span className={styles["dropdown-item-text"]}>{value}</span>
                    </div>
                );
            })}
        </>
    );
});

DropdownList.displayName = 'DropdownList';

function CustomTable({ 
    columns = [], 
    data = [], 
    onRowSelect = null,
    selectedRows = [],
    rowKey = "id",
    enableSearch = true,
    enableSorting = true,
    enableSelection = true,
    enableShowEntries = true,
    enableFilter = true,
    enableColumnFilters = true,
    enableExport = true,
    enablePagination = true,
    showEntriesOptions = DEFAULT_SHOW_ENTRIES_OPTIONS,
    defaultEntriesPerPage = 500,
    searchPlaceholder = "Search",
    onExport = null,
    exportType = null, // Export type for file download (e.g., "Holdings", "Orders", "BulkOrders")
    onFilter = null,
    className = "",
    enableRecentSearch = true,
    recentSearchStorageKey = "customTable_recentSearches",
    maxRecentSearches = 10,
    // Client code / symbol-style filters: inner search auto-focuses when opened (Order Book, Position Book,
    // Holding Book, Bulk Order, and any other screen passing keys here).
    columnsWithDropdown = [],
    secondaryTabs = null, // Array of {label, value} for secondary tabs
    activeSecondaryTab = null, // Current active secondary tab value
    onSecondaryTabChange = null, // Callback function for tab change
    toolbarFilterOptions = null, // Array of {label, value} — dropdown in toolbar (e.g. position filter)
    toolbarFilterValue = "",
    onToolbarFilterChange = null,
    toolbarFilterPlaceholder = "Filter",
    // Date filter props
    enableDateFilter = false,
    dateFilterOptions = [], // Array of {label, value} for date filter options
    dateFilterValue = "",
    onDateFilterChange = null,
    // Strategy filter props
    enableStrategyFilter = false,
    strategyFilterOptions = [], // Array of {label, value} for strategy filter options
    strategyFilterValue = "",
    onStrategyFilterChange = null,
    toolbarRefresh = null,
    /** When set, header drag handles reorder columns and persist via table column prefs. */
    tableId = null,
    enableColumnReorder = undefined,
}) {
    const POSITIVE_COLOR = "var(--color-positive)";
    const NEGATIVE_COLOR = "var(--color-negative)";
    const columnReorderEnabled =
        enableColumnReorder !== undefined ? Boolean(enableColumnReorder) : Boolean(tableId);

    const [globalSearch, setGlobalSearch] = useState("");
    const [columnFilters, setColumnFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [selectedRowIds, setSelectedRowIds] = useState(selectedRows || []);
    const [entriesPerPage, setEntriesPerPage] = useState(() =>
        resolveInitialEntriesPerPage(defaultEntriesPerPage, showEntriesOptions)
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [recentSearches, setRecentSearches] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState({}); // Track which column dropdowns are open
    const [dropdownSearch, setDropdownSearch] = useState({}); // Track search text in each dropdown
    const [activeDragColumnKey, setActiveDragColumnKey] = useState(null);
    const anchorRefs = useRef({}); // Refs for dropdown positioning
    const dropdownSearchInputRefs = useRef({});
    const prevSecondaryTabRef = useRef(activeSecondaryTab);
    const prevToolbarFilterRef = useRef(toolbarFilterValue);

    const columnKeys = useMemo(
        () => columns.map((col) => col.key).filter(Boolean),
        [columns]
    );

    const activeDragColumn = useMemo(
        () => (activeDragColumnKey ? columns.find((c) => c.key === activeDragColumnKey) : null),
        [activeDragColumnKey, columns]
    );

    const columnReorderSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleColumnDragStart = useCallback((event) => {
        setActiveDragColumnKey(String(event.active.id));
    }, []);

    const handleColumnDragCancel = useCallback(() => {
        setActiveDragColumnKey(null);
    }, []);

    const handleColumnDragEnd = useCallback(
        (event) => {
            const { active, over } = event;
            setActiveDragColumnKey(null);
            if (!columnReorderEnabled || !tableId || !over) return;
            const activeId = String(active.id);
            const overId = String(over.id);
            if (activeId === overId) return;
            const keys = columns.map((c) => c.key).filter(Boolean);
            const fromIndex = keys.indexOf(activeId);
            const toIndex = keys.indexOf(overId);
            if (fromIndex < 0 || toIndex < 0) return;
            saveColumnKeys(tableId, moveKeyInList(keys, fromIndex, toIndex));
        },
        [columnReorderEnabled, tableId, columns]
    );

    // Memoize unique column values to avoid recalculation
    const uniqueColumnValues = useMemo(() => {
        const cache = {};
        columnsWithDropdown.forEach(columnKey => {
            const values = new Set();
            data.forEach(row => {
                const value = row[columnKey];
                if (value !== null && value !== undefined && value !== '') {
                    values.add(String(value));
                }
            });
            cache[columnKey] = Array.from(values).sort();
        });
        return cache;
    }, [data, columnsWithDropdown]);

    // Memoize dropdown filter check
    const dropdownFilterMap = useMemo(() => {
        return new Set(columnsWithDropdown);
    }, [columnsWithDropdown]);

    // Helper function to check if a value is numeric
    const isNumeric = useCallback((value) => {
        if (value === null || value === undefined) return false;
        return !isNaN(value) && !isNaN(parseFloat(value));
    }, []);

    // Helper function to parse numeric range filters
    const parseNumericFilter = useCallback((filterValue, cellValue) => {
        if (!isNumeric(cellValue)) return false;
        
        const numValue = parseFloat(cellValue);
        const filter = filterValue.trim();
        const filterLen = filter.length;
        
        // Early return for empty filter
        if (filterLen === 0) return false;
        
        // Range: "100-500"
        if (filterLen > 2 && filter.includes('-') && filter[0] !== '>' && filter[0] !== '<') {
            const dashIndex = filter.indexOf('-');
            const min = parseFloat(filter.substring(0, dashIndex));
            const max = parseFloat(filter.substring(dashIndex + 1));
            if (!isNaN(min) && !isNaN(max)) {
                return numValue >= min && numValue <= max;
            }
        }
        
        // Greater than or equal: ">=100"
        if (filterLen >= 3 && filter.startsWith('>=')) {
            const threshold = parseFloat(filter.substring(2));
            return !isNaN(threshold) && numValue >= threshold;
        }
        // Greater than: ">100"
        if (filterLen >= 2 && filter.startsWith('>')) {
            const threshold = parseFloat(filter.substring(1));
            return !isNaN(threshold) && numValue > threshold;
        }
        
        // Less than or equal: "<=500"
        if (filterLen >= 3 && filter.startsWith('<=')) {
            const threshold = parseFloat(filter.substring(2));
            return !isNaN(threshold) && numValue <= threshold;
        }
        // Less than: "<500"
        if (filterLen >= 2 && filter.startsWith('<')) {
            const threshold = parseFloat(filter.substring(1));
            return !isNaN(threshold) && numValue < threshold;
        }
        
        // Exact match for numbers
        const exact = parseFloat(filter);
        return !isNaN(exact) && numValue === exact;
    }, [isNumeric]);

    // Helper function to parse advanced search operators
    const parseAdvancedSearch = useCallback((searchTerm, cellValue) => {
        return textMatchesSearch(cellValue, searchTerm);
    }, []);

    // Check if a column should have dropdown filter
    const hasDropdownFilter = useCallback((columnKey) => {
        return dropdownFilterMap.has(columnKey);
    }, [dropdownFilterMap]);

    // Helper function to highlight search matches in text
    const highlightText = useCallback((text, searchTerm) => {
        return highlightSearchTokens(text, searchTerm);
    }, []);

    // Load recent searches from localStorage on mount
    useEffect(() => {
        if (enableRecentSearch && recentSearchStorageKey) {
            try {
                const stored = localStorage.getItem(recentSearchStorageKey);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    setRecentSearches(Array.isArray(parsed) ? parsed : []);
                }
            } catch (error) {
                console.error("Error loading recent searches:", error);
            }
        }
    }, [enableRecentSearch, recentSearchStorageKey]);

    // Sync internal state with prop when it changes externally
    useEffect(() => {
        setSelectedRowIds(selectedRows || []);
    }, [selectedRows]);

    // Clear selection, search, column filters, and sort when secondary tab changes (different tab = different data)
    useEffect(() => {
        if (secondaryTabs == null || secondaryTabs.length === 0) return;
        if (prevSecondaryTabRef.current !== activeSecondaryTab) {
            prevSecondaryTabRef.current = activeSecondaryTab;
            setSelectedRowIds([]);
            setGlobalSearch("");
            setColumnFilters({});
            setSortConfig({ key: null, direction: "asc" });
            setCurrentPage(1);
            setDropdownOpen({});
            setDropdownSearch({});
            if (onRowSelect) {
                onRowSelect([], []);
            }
        }
    }, [activeSecondaryTab, secondaryTabs, onRowSelect]);

    // Clear selection, search, column filters, and sort when toolbar filter changes
    useEffect(() => {
        if (toolbarFilterOptions == null || toolbarFilterOptions.length === 0) return;
        if (prevToolbarFilterRef.current !== toolbarFilterValue) {
            prevToolbarFilterRef.current = toolbarFilterValue;
            setSelectedRowIds([]);
            setGlobalSearch("");
            setColumnFilters({});
            setSortConfig({ key: null, direction: "asc" });
            setCurrentPage(1);
            setDropdownOpen({});
            setDropdownSearch({});
            if (onRowSelect) {
                onRowSelect([], []);
            }
        }
    }, [toolbarFilterValue, toolbarFilterOptions, onRowSelect]);

    // Reset to page 1 when entries per page changes
    useEffect(() => {
        setCurrentPage(1);
    }, [entriesPerPage]);

    // Handle row selection
    const getSelectedRowsByIds = useCallback((ids) => {
        if (!ids || ids.length === 0) return [];
        // Use original data so selection is stable across filters/pagination
        const idSet = new Set(ids);
        return data.filter((row) => idSet.has(row[rowKey]));
    }, [data, rowKey]);

    // Memoize column filter keys and filter state for faster lookup
    const columnFilterKeys = useMemo(() => Object.keys(columnFilters), [columnFilters]);
    const hasColumnFilters = useMemo(() => columnFilterKeys.length > 0, [columnFilterKeys]);
    const hasGlobalSearch = useMemo(() => Boolean(globalSearch?.trim()), [globalSearch]);
    
    // Memoize dropdown filter sets for O(1) lookup
    const dropdownFilterSets = useMemo(() => {
        const sets = {};
        columnFilterKeys.forEach(colKey => {
            const filterValue = columnFilters[colKey];
            if (Array.isArray(filterValue) && filterValue.length > 0) {
                sets[colKey] = new Set(filterValue);
            }
        });
        return sets;
    }, [columnFilters, columnFilterKeys]);

    // Memoize sort comparison function
    const sortCompare = useCallback((a, b, sortKey, direction) => {
        const aValue = a[sortKey];
        const bValue = b[sortKey];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === "number" && typeof bValue === "number") {
            return direction === "asc" ? aValue - bValue : bValue - aValue;
        }
        
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        return direction === "asc" 
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
    }, []);

    // Filter and sort data
    const processedData = useMemo(() => {
        // Early return if no filters
        if (!hasGlobalSearch && !hasColumnFilters) {
            // Only sort if needed
            if (enableSorting && sortConfig.key) {
                return [...data].sort((a, b) => sortCompare(a, b, sortConfig.key, sortConfig.direction));
            }
            return data;
        }

        const searchTerm = hasGlobalSearch ? globalSearch.trim() : '';
        const hasColumnSearch = searchTerm.includes(':');
        let matchingColumn = null;
        let columnSearchValue = '';

        // Pre-process column-specific search
        if (hasColumnSearch) {
            const colonIndex = searchTerm.indexOf(':');
            const colKey = searchTerm.substring(0, colonIndex).trim();
            columnSearchValue = searchTerm.substring(colonIndex + 1).trim();
            matchingColumn = columns.find(col => 
                col.key.toLowerCase() === colKey.toLowerCase() || 
                col.label.toLowerCase() === colKey.toLowerCase()
            );
        }

        const filtered = data.filter((row) => {
            // Global search optimization
            if (hasGlobalSearch) {
                if (hasColumnSearch && matchingColumn) {
                    // Column-specific search
                    const cellValue = row[matchingColumn.key];
                    const columnMatches = isNumeric(cellValue)
                        ? parseNumericFilter(columnSearchValue, cellValue) ||
                          parseAdvancedSearch(columnSearchValue, cellValue)
                        : parseAdvancedSearch(columnSearchValue, cellValue);
                    if (!columnMatches) return false;
                } else if (hasColumnSearch) {
                    if (!rowMatchesMultiTermSearch(row, columns, columnSearchValue)) return false;
                } else if (!rowMatchesMultiTermSearch(row, columns, searchTerm)) {
                    let numericHit = false;
                    if (!/\s/.test(searchTerm)) {
                        for (let i = 0; i < columns.length; i++) {
                            const cellValue = row[columns[i].key];
                            if (isNumeric(cellValue) && parseNumericFilter(searchTerm, cellValue)) {
                                numericHit = true;
                                break;
                            }
                        }
                    }
                    if (!numericHit) return false;
                }
            }

            // Column filters optimization - only check columns with filters
            if (hasColumnFilters) {
                for (let i = 0; i < columnFilterKeys.length; i++) {
                    const colKey = columnFilterKeys[i];
                    const filterValue = columnFilters[colKey];
                    if (!filterValue) continue;
                    
                    const cellValue = row[colKey];
                    
                    // Handle dropdown multi-select (O(1) lookup with Set)
                    if (Array.isArray(filterValue)) {
                        if (filterValue.length > 0) {
                            const filterSet = dropdownFilterSets[colKey];
                            if (filterSet && !filterSet.has(String(cellValue))) {
                                return false;
                            }
                        }
                        continue;
                    }
                    
                    // Numeric filter (also allow digit substring / comma-insensitive match like "16" on 168350 or "1,68,350")
                    if (isNumeric(cellValue)) {
                        if (
                            !parseNumericFilter(filterValue, cellValue) &&
                            !parseAdvancedSearch(filterValue, cellValue)
                        ) {
                            return false;
                        }
                        continue;
                    }
                    
                    // Text filter
                    if (!parseAdvancedSearch(filterValue, cellValue)) return false;
                }
            }

            return true;
        });

        // Sorting (only if needed)
        if (enableSorting && sortConfig.key && filtered.length > 1) {
            filtered.sort((a, b) => sortCompare(a, b, sortConfig.key, sortConfig.direction));
        }

        return filtered;
    }, [data, hasGlobalSearch, globalSearch, hasColumnFilters, columnFilterKeys, columnFilters, dropdownFilterSets, columns, sortConfig, enableSorting, parseNumericFilter, parseAdvancedSearch, isNumeric, sortCompare]);

    // Pagination (memoized)
    const totalPages = useMemo(() => Math.ceil(processedData.length / entriesPerPage), [processedData.length, entriesPerPage]);
    const startIndex = useMemo(() => (currentPage - 1) * entriesPerPage, [currentPage, entriesPerPage]);
    const endIndex = useMemo(() => startIndex + entriesPerPage, [startIndex, entriesPerPage]);
    const paginatedData = useMemo(() => {
        return enablePagination 
            ? processedData.slice(startIndex, endIndex)
            : processedData;
    }, [enablePagination, processedData, startIndex, endIndex]);

    // Memoize selected IDs set for O(1) lookup
    const selectedIdsSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds]);

    const handleSelectAll = useCallback((event) => {
        if (event.target.checked) {
            const allIds = paginatedData.map((row) => row[rowKey]);
            setSelectedRowIds(allIds);
            if (onRowSelect) {
                onRowSelect(allIds, getSelectedRowsByIds(allIds));
            }
        } else {
            setSelectedRowIds([]);
            if (onRowSelect) {
                onRowSelect([], []);
            }
        }
    }, [paginatedData, rowKey, onRowSelect, getSelectedRowsByIds]);

    const handleSelectRow = useCallback((rowId) => {
        const isSelected = selectedIdsSet.has(rowId);
        const newSelected = isSelected
            ? selectedRowIds.filter((id) => id !== rowId)
            : [...selectedRowIds, rowId];
        setSelectedRowIds(newSelected);
        if (onRowSelect) {
            onRowSelect(newSelected, getSelectedRowsByIds(newSelected));
        }
    }, [selectedRowIds, selectedIdsSet, onRowSelect, getSelectedRowsByIds]);

    // Handle sorting (memoized)
    const handleSort = useCallback((columnKey) => {
        setSortConfig((prev) => {
            let direction = "asc";
            if (prev.key === columnKey && prev.direction === "asc") {
                direction = "desc";
            }
            return { key: columnKey, direction };
        });
    }, []);

    // Save search to recent searches (memoized)
    const saveToRecentSearches = useCallback((searchTerm) => {
        if (!enableRecentSearch || !searchTerm || !searchTerm.trim()) return;
        
        const trimmedTerm = searchTerm.trim();
        setRecentSearches((prev) => {
            // Remove duplicates and add to beginning
            const filtered = prev.filter((item) => item !== trimmedTerm);
            const updated = [trimmedTerm, ...filtered].slice(0, maxRecentSearches);
            
            // Save to localStorage
            try {
                if (recentSearchStorageKey) {
                    localStorage.setItem(recentSearchStorageKey, JSON.stringify(updated));
                }
            } catch (error) {
                console.error("Error saving recent searches:", error);
            }
            
            return updated;
        });
    }, [enableRecentSearch, maxRecentSearches, recentSearchStorageKey]);

    // Remove a recent search (memoized)
    const handleRemoveRecentSearch = useCallback((event, searchTerm) => {
        event.stopPropagation();
        setRecentSearches((prev) => {
            const updated = prev.filter((item) => item !== searchTerm);
            
            // Update localStorage
            try {
                if (recentSearchStorageKey) {
                    localStorage.setItem(recentSearchStorageKey, JSON.stringify(updated));
                }
            } catch (error) {
                console.error("Error updating recent searches:", error);
            }
            
            return updated;
        });
    }, [recentSearchStorageKey]);

    const handleGlobalSearchChange = useCallback((value) => {
        setGlobalSearch(value);
        setCurrentPage(1);
    }, []);

    const handleClearSearch = useCallback(() => {
        setGlobalSearch("");
        setCurrentPage(1);
    }, []);

    // Handle when user selects from autocomplete or presses Enter
    const handleSearchSubmit = useCallback((value) => {
        if (value && value.trim()) {
            saveToRecentSearches(value);
        }
    }, [saveToRecentSearches]);

    const handleColumnFilterChange = useCallback((columnKey, value) => {
        setColumnFilters((prev) => {
            if (value) {
                return { ...prev, [columnKey]: value };
            }
            const updated = { ...prev };
            delete updated[columnKey];
            return updated;
        });
        setCurrentPage(1);
    }, []);

    // Handle dropdown checkbox selection
    const handleDropdownValueToggle = useCallback((columnKey, value) => {
        setColumnFilters((prev) => {
            const currentSelection = Array.isArray(prev[columnKey]) ? prev[columnKey] : [];
            const valueIndex = currentSelection.indexOf(value);
            
            if (valueIndex >= 0) {
                // Remove value
                if (currentSelection.length === 1) {
                    const updated = { ...prev };
                    delete updated[columnKey];
                    return updated;
                }
                const newSelection = [...currentSelection];
                newSelection.splice(valueIndex, 1);
                return { ...prev, [columnKey]: newSelection };
            } else {
                // Add value
                return { ...prev, [columnKey]: [...currentSelection, value] };
            }
        });
        setCurrentPage(1);
    }, []);

    // Handle dropdown open/close - ensure only one dropdown is open at a time
    const handleDropdownToggle = useCallback((columnKey) => {
        setDropdownOpen((prev) => {
            const isCurrentlyOpen = prev[columnKey];
            // If opening this dropdown, close all others first
            if (!isCurrentlyOpen) {
                setDropdownSearch((p) => ({ ...p, [columnKey]: '' }));
                return { [columnKey]: true };
            }
            // If closing, just close this one
            const newState = { ...prev };
            newState[columnKey] = false;
            return newState;
        });
    }, []);

    // Handle dropdown search text change
    const handleDropdownSearchChange = useCallback((columnKey, searchText) => {
        setDropdownSearch((prev) => ({ ...prev, [columnKey]: searchText }));
    }, []);

    // When a dropdown-column filter opens, focus the inner search (Popper may mount after paint — retry).
    useEffect(() => {
        const openEntry = Object.entries(dropdownOpen).find(([, isOpen]) => isOpen);
        if (!openEntry) return undefined;
        const [openColumnKey] = openEntry;
        let cancelled = false;

        const focusInnerSearch = () => {
            if (cancelled) return;
            const input = dropdownSearchInputRefs.current[openColumnKey];
            if (input && typeof input.focus === "function") {
                input.focus({ preventScroll: true });
            }
        };

        const timeoutIds = [0, 32, 96].map((ms) => window.setTimeout(focusInnerSearch, ms));

        return () => {
            cancelled = true;
            timeoutIds.forEach((id) => window.clearTimeout(id));
        };
    }, [dropdownOpen]);

    const handleExport = useCallback(() => {
        // If exportType is provided, use fileDownloader
        if (exportType) {
            const url = `/api/analytics/download-excel/${exportType}/`;
            downloadFile(url);
        }
        
        // Also call onExport callback if provided (for backward compatibility)
        if (onExport) {
            onExport(processedData, selectedRowIds);
        }
    }, [exportType, onExport, processedData, selectedRowIds]);

    const handleFilterClick = useCallback(() => {
        if (onFilter) {
            onFilter({});
        }
    }, [onFilter]);

    // Selection checks using Set for O(1) lookup
    const isRowSelected = useCallback((rowId) => selectedIdsSet.has(rowId), [selectedIdsSet]);
    
    const isAllSelected = useMemo(() => {
        if (paginatedData.length === 0) return false;
        return paginatedData.every(row => selectedIdsSet.has(row[rowKey]));
    }, [paginatedData, selectedIdsSet, rowKey]);
    
    const isIndeterminate = useMemo(() => {
        if (selectedRowIds.length === 0 || isAllSelected) return false;
        return paginatedData.some(row => selectedIdsSet.has(row[rowKey]));
    }, [selectedRowIds.length, isAllSelected, paginatedData, selectedIdsSet, rowKey]);

    const columnKeysSig = useMemo(() => columns.map((c) => c.key).join("|"), [columns]);

    const tableContainerRef = useRef(null);
    const [stickyColumnLefts, setStickyColumnLefts] = useState([0, 0, 0]);

    /** First three table columns: with selection = checkbox + first two data columns; without = first three data columns. */
    const shouldStickyDataColumn = useCallback(
        (colIndex) => {
            if (colIndex < 0 || colIndex >= columns.length) return false;
            if (enableSelection) return colIndex <= 1;
            return colIndex <= 2;
        },
        [columns.length, enableSelection]
    );

    /** Slot 0 = leftmost pinned (highest z-index); maps to stickyCol0..stickyCol2 */
    const getStickySlotForDataColumn = useCallback(
        (colIndex) => {
            if (!shouldStickyDataColumn(colIndex)) return -1;
            return enableSelection ? colIndex + 1 : colIndex;
        },
        [enableSelection, shouldStickyDataColumn]
    );

    /** Index into measured stickyColumnLefts (same order as thead cells for the first three columns). */
    const getStickyMeasureIndexForDataColumn = useCallback(
        (colIndex) => {
            if (!shouldStickyDataColumn(colIndex)) return -1;
            return enableSelection ? colIndex + 1 : colIndex;
        },
        [enableSelection, shouldStickyDataColumn]
    );

    /** Rightmost pinned column only (slot 0–2) — single box-shadow between fixed and scrolling areas. */
    const isLastStickySlot = useCallback(
        (slot) => {
            if (slot < 0 || slot > 2) return false;
            if (enableSelection) {
                if (columns.length >= 2) return slot === 2;
                if (columns.length === 1) return slot === 1;
                return slot === 0;
            }
            if (columns.length >= 3) return slot === 2;
            if (columns.length === 2) return slot === 1;
            if (columns.length === 1) return slot === 0;
            return false;
        },
        [enableSelection, columns.length]
    );

    useLayoutEffect(() => {
        const root = tableContainerRef.current;
        if (!root) return;

        /**
         * Measure cumulative `left` for pinned columns from a real data row when possible so
         * `left` matches body column widths (thead filters can differ and misalign headers vs cells).
         * Fallback: thead row.
         */
        const measure = () => {
            const tbodyRows = root.querySelectorAll("tbody tr");
            let sourceRow = null;
            for (const tr of tbodyRows) {
                const tds = tr.querySelectorAll("td");
                if (tds.length > 1) {
                    sourceRow = tr;
                    break;
                }
            }
            if (!sourceRow) {
                sourceRow = root.querySelector("thead tr");
            }
            if (!sourceRow) return;

            const cells = Array.from(sourceRow.children);
            const n = Math.min(3, cells.length);
            let acc = 0;
            const lefts = [0, 0, 0];
            for (let i = 0; i < n; i++) {
                lefts[i] = acc;
                acc += cells[i].offsetWidth;
            }
            setStickyColumnLefts((prev) => {
                if (prev[0] === lefts[0] && prev[1] === lefts[1] && prev[2] === lefts[2]) return prev;
                return lefts;
            });
        };

        const runMeasure = () => {
            requestAnimationFrame(measure);
        };

        runMeasure();
        const ro = new ResizeObserver(runMeasure);
        ro.observe(root);
        const syncTheadTbody = () => {
            const theadRow = root.querySelector("thead tr");
            const tbodyRow = root.querySelector("tbody tr");
            if (theadRow) {
                ro.observe(theadRow);
                Array.from(theadRow.children).forEach((cell) => ro.observe(cell));
            }
            if (tbodyRow && tbodyRow.querySelectorAll("td").length > 1) {
                ro.observe(tbodyRow);
                Array.from(tbodyRow.children).forEach((cell) => ro.observe(cell));
            }
        };
        syncTheadTbody();
        window.addEventListener("resize", runMeasure);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", runMeasure);
        };
    }, [enableSelection, columnKeysSig, paginatedData.length]);

    // Reusable sort icon component
    const SortIconComponent = useCallback(() => (
        <GlobalIcon iconName="TableSortIcon" className={styles["sort-icon"]} />
    ), []);

    // Get cell styling from column config
    const getCellStyle = (column, value, row) => {
        if (column.styles) {
            if (typeof column.styles === "function") {
                return column.styles(value, row);
            }
            return column.styles;
        }
        return {};
    };

    const renderCellValue = useCallback((col, row) => {
        const value = row[col.key];

        if (col.render) {
            return col.render(value, row);
        }

        const colKey = String(col.key ?? "").toLowerCase();
        const transactionSide = String(value ?? "").trim().toUpperCase();
        const isTransactionColumn =
            colKey === "transaction_type" ||
            colKey === "transaction" ||
            colKey === "trans" ||
            colKey === "buy_sell" ||
            colKey === "side";
        if (isTransactionColumn && (transactionSide === "BUY" || transactionSide === "SELL")) {
            const color = transactionSide === "BUY" ? POSITIVE_COLOR : NEGATIVE_COLOR;
            const display = value ?? "-";
            const highlighted = globalSearch ? highlightText(String(display), globalSearch) : display;
            return <span style={{ color, fontWeight: 600 }}>{highlighted}</span>;
        }

        const isProfitColumn =
            colKey === "pnl" ||
            colKey === "pnl_pct" ||
            colKey === "realised_pnl" ||
            colKey === "unrealised_pnl" ||
            colKey === "total_pnl";
        const qtyTone =
            tableId === TABLE_COLUMN_TABLE_IDS.POSITION_BOOK
                ? positionBookValueTone(colKey, value)
                : null;
        const isSignedQtyColumn = Boolean(qtyTone);

        if (isProfitColumn || isSignedQtyColumn) {
            const numeric = parseNumericCellValue(value);
            const color = isSignedQtyColumn
                ? qtyTone === "negative"
                    ? NEGATIVE_COLOR
                    : POSITIVE_COLOR
                : !Number.isNaN(numeric) && numeric !== 0
                    ? numeric < 0
                        ? NEGATIVE_COLOR
                        : POSITIVE_COLOR
                    : undefined;
            const display =
                colKey === "pnl_pct"
                    ? Number.isNaN(numeric)
                        ? value ?? "-"
                        : `${numeric.toFixed(2)}%`
                    : typeof value === "number"
                    ? value.toLocaleString("en-IN")
                    : value ?? "-";
            const highlighted = globalSearch ? highlightText(String(display), globalSearch) : display;
            return <span style={color ? { color, fontWeight: 600 } : undefined}>{highlighted}</span>;
        }

        if (typeof value === "number") {
            const formatted = value.toLocaleString("en-IN");
            const highlighted = globalSearch ? highlightText(formatted, globalSearch) : formatted;
            // If highlighting returned an array/element, wrap it, otherwise return as string
            if (typeof highlighted === 'object') {
                return <span>{highlighted}</span>;
            }
            return highlighted;
        }

        const textValue = value ?? "-";
        const highlighted = globalSearch ? highlightText(textValue, globalSearch) : textValue;
        // If highlighting returned an array/element, return it, otherwise return as string
        if (typeof highlighted === 'object') {
            return highlighted;
        }
        return highlighted;
    }, [globalSearch, highlightText, tableId]);

    // Simple calculations - no memoization needed
    const startEntry = processedData.length === 0 ? 0 : startIndex + 1;
    const endEntry = Math.min(endIndex, processedData.length);

    // Memoize pagination handlers
    const handlePreviousPage = useCallback(() => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    }, []);

    const handleNextPage = useCallback(() => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    }, [totalPages]);

    const handleEntriesPerPageChange = useCallback((e) => {
        setEntriesPerPage(Number(e.target.value));
    }, []);

    // Memoize filterOptions for Autocomplete
    const filterAutocompleteOptions = useCallback((options, state) => {
        // Show all recent searches when input is empty or matches
        if (!state.inputValue) {
            return options;
        }
        // Filter options that match the input
        return options.filter(option =>
            option.toLowerCase().includes(state.inputValue.toLowerCase())
        );
    }, []);

    return (
        <div className={styles["custom-table-wrapper"]}>
            {/* Secondary Tabs */}
            {secondaryTabs && secondaryTabs.length > 0 && (
                <div className={styles["secondary-tabs-container"]}>
                    <Tabs
                        value={activeSecondaryTab || false}
                        onChange={onSecondaryTabChange}
                        className={styles["secondary-tabs"]}
                    >
                        {secondaryTabs.map((tab) => (
                            <Tab
                                key={tab.value}
                                label={tab.label}
                                value={tab.value}
                                className={styles["secondary-tab"]}
                                disableRipple
                            />
                        ))}
                    </Tabs>
                </div>
            )}
            {/* Table Controls */}
            <div className={styles["table-controls"]}>
                <div className={styles["controls-left"]}>
                    {enableShowEntries && (
                        <Select
                            value={entriesPerPage}
                            onChange={handleEntriesPerPageChange}
                            className={styles["show-entries-select"]}
                            size="small"
                            inputProps={{ "aria-label": "Number of rows per page" }}
                            renderValue={(value) => (
                                <div className={styles["show-entries-content"]}>
                                    <span className={styles["show-entries-value"]}>{value}</span>
                                    <GlobalIcon iconName="TableSortIcon" className={styles["show-entries-icon"]} />
                                </div>
                            )}
                            IconComponent={() => null}
                            MenuProps={{
                                PaperProps: {
                                    className: styles["show-entries-dropdown-paper"]
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'right',
                                },
                                disableAutoFocusItem: true,
                                transitionDuration: 0
                            }}
                        >
                            {showEntriesOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {toolbarFilterOptions && toolbarFilterOptions.length > 0 && (
                        <Select
                            value={toolbarFilterValue}
                            onChange={onToolbarFilterChange}
                            className={styles["table-filter-select"]}
                            size="small"
                            inputProps={{ "aria-label": toolbarFilterPlaceholder }}
                            renderValue={(selected) => {
                                const option = toolbarFilterOptions.find((opt) => opt.value === selected);
                                return option ? option.label : (
                                    <span className={styles["filter-placeholder"]}>{toolbarFilterPlaceholder}</span>
                                );
                            }}
                            MenuProps={{
                                PaperProps: {
                                    className: styles["filter-menu-paper"],
                                },
                                MenuListProps: {
                                    className: styles["filter-menu-list"],
                                },
                            }}
                        >
                            {toolbarFilterOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    className={styles["filter-menu-item"]}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {enableSearch && (
                        <div className={styles["search-container"]}>
                            {enableRecentSearch ? (
                                <Autocomplete
                                    freeSolo
                                    openOnFocus
                                    options={recentSearches}
                                    value={globalSearch}
                                    filterOptions={filterAutocompleteOptions}
                                    onInputChange={(event, newValue) => {
                                        handleGlobalSearchChange(newValue);
                                    }}
                                    onChange={(event, newValue) => {
                                        const value = typeof newValue === 'string' ? newValue : newValue || '';
                                        handleGlobalSearchChange(value);
                                        if (value) {
                                            handleSearchSubmit(value);
                                        }
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' && globalSearch.trim()) {
                                            handleSearchSubmit(globalSearch);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder={searchPlaceholder}
                                            className={styles["search-input"]}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
                                                        </InputAdornment>
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                                endAdornment: globalSearch ? (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Clear search">
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSearch}
                                                                className={styles["clear-search-button"]}
                                                                edge="end"
                                                            >
                                                                <span className={styles["clear-icon"]}>×</span>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ) : null,
                                                disableUnderline: true,
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box
                                            component="li"
                                            {...props}
                                            className={styles["recent-search-item"]}
                                        >
                                            <GlobalIcon 
                                                iconName="SearchIcon" 
                                                className={styles["recent-search-icon"]} 
                                            />
                                            <span className={styles["recent-search-text"]}>
                                                {option}
                                            </span>
                                            <IconButton
                                                size="small"
                                                className={styles["recent-search-remove"]}
                                                onClick={(e) => handleRemoveRecentSearch(e, option)}
                                                onMouseDown={(e) => e.preventDefault()}
                                            >
                                                <span className={styles["remove-icon"]}>×</span>
                                            </IconButton>
                                        </Box>
                                    )}
                                    ListboxProps={{
                                        className: styles["recent-searches-listbox"],
                                    }}
                                    PaperComponent={({ children, ...other }) => (
                                        <Paper {...other} className={styles["recent-searches-dropdown"]}>
                                            {recentSearches.length > 0 && (
                                                <div className={styles["recent-searches-header"]}>
                                                    Recent Search
                                                </div>
                                            )}
                                            {children}
                                        </Paper>
                                    )}
                                    className={styles["search-autocomplete"]}
                                    noOptionsText={recentSearches.length === 0 ? "" : "No recent searches"}
                                />
                            ) : (
                                <TextField
                                    size="small"
                                    placeholder={searchPlaceholder}
                                    value={globalSearch}
                                    onChange={(e) => handleGlobalSearchChange(e.target.value)}
                                    className={styles["search-input"]}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: globalSearch ? (
                                            <InputAdornment position="end">
                                                <Tooltip title="Clear search">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleClearSearch}
                                                        className={styles["clear-search-button"]}
                                                        edge="end"
                                                    >
                                                        <span className={styles["clear-icon"]}>×</span>
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                        disableUnderline: true,
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {enableFilter === true && (
                        <Button
                            variant="outlined"
                            onClick={handleFilterClick}
                            className={styles["control-button"]}
                            endIcon={<GlobalIcon iconName="ChevronDownIcon" className={styles["button-icon"]} />}
                        >
                            Filter
                        </Button>
                    )}
                    {enableExport && (
                        <Button
                            variant="outlined"
                            onClick={handleExport}
                            className={styles["control-button"]}
                            endIcon={<GlobalIcon iconName="DownloadIcon" className={styles["button-icon"]} />}
                        >
                            Export
                        </Button>
                    )}
                </div>
                <div className={styles["controls-right"]}>
                    {toolbarRefresh}
                    {enableDateFilter && (
                        <Select
                            value={dateFilterValue}
                            onChange={(e) => onDateFilterChange && onDateFilterChange(e.target.value)}
                            className={styles["table-filter-select"]}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span className={styles["filter-placeholder"]}>Date Filter</span>;
                                }
                                const option = dateFilterOptions.find(opt => opt.value === selected);
                                return option ? option.label : selected;
                            }}
                            MenuProps={{
                                PaperProps: {
                                    className: styles["filter-menu-paper"]
                                },
                                MenuListProps: {
                                    className: styles["filter-menu-list"]
                                }
                            }}
                        >
                            {dateFilterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value} className={styles["filter-menu-item"]}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {enableStrategyFilter && (
                        <Select
                            value={strategyFilterValue}
                            onChange={(e) => onStrategyFilterChange && onStrategyFilterChange(e.target.value)}
                            className={styles["table-filter-select"]}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span className={styles["filter-placeholder"]}>Change Strategy</span>;
                                }
                                const option = strategyFilterOptions.find(opt => opt.value === selected);
                                return option ? option.label : selected;
                            }}
                            MenuProps={{
                                PaperProps: {
                                    className: styles["filter-menu-paper"]
                                },
                                MenuListProps: {
                                    className: styles["filter-menu-list"]
                                }
                            }}
                        >
                            {strategyFilterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value} className={styles["filter-menu-item"]}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {enablePagination && (
                        <div className={styles["pagination-info"]}>
                            <span className={styles["pagination-text"]}>
                                {startEntry} - {endEntry} of {processedData.length}
                                {data.length !== processedData.length
                                    ? ` (total ${data.length})`
                                    : ""}
                            </span>
                            <div className={styles["pagination-controls"]}>
                                <IconButton
                                    size="small"
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    className={styles["pagination-button"]}
                                >
                                    &lt;
                                </IconButton>
                                <span className={styles["pagination-page"]}>{currentPage}</span>
                                <IconButton
                                    size="small"
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    className={styles["pagination-button"]}
                                >
                                    &gt;
                                </IconButton>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Table */}
            <DndContext
                sensors={columnReorderSensors}
                collisionDetection={closestCenter}
                onDragStart={columnReorderEnabled ? handleColumnDragStart : undefined}
                onDragEnd={columnReorderEnabled ? handleColumnDragEnd : undefined}
                onDragCancel={columnReorderEnabled ? handleColumnDragCancel : undefined}
            >
            <TableContainer
                ref={tableContainerRef}
                component={Paper}
                className={`${styles["table-container"]} ${className}${
                    activeDragColumnKey ? ` ${styles["table-container--reordering"]}` : ""
                }`}
            >
                {/* stickyHeader off: MUI would apply sticky top+z-index to all th and conflicts with horizontal sticky + our header z-index */}
                <Table className={styles["table"]}>
                    <TableHead>
                        <TableRow className={styles["header-row"]}>
                            {enableSelection && (
                                <TableCell
                                    className={`${styles["checkbox-cell"]} ${styles.stickyPinned} ${styles.stickyCol0}${
                                        isLastStickySlot(0) ? ` ${styles.stickyPinnedLast}` : ""
                                    }`}
                                    padding="checkbox"
                                    style={{ left: stickyColumnLefts[0] }}
                                >
                                    <Checkbox
                                        indeterminate={isIndeterminate}
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        className={styles["checkbox"]}
                                        sx={{
                                            '&.Mui-checked': {
                                                color: 'rgba(79, 70, 229, 0.60)',
                                            },
                                            '&.Mui-checked .MuiSvgIcon-root': {
                                                fill: 'rgba(79, 70, 229, 0.60)',
                                                color: 'rgba(79, 70, 229, 0.60)',
                                            },
                                        }}
                                    />
                                </TableCell>
                            )}
                            <SortableContext
                                items={columnKeys}
                                strategy={horizontalListSortingStrategy}
                                disabled={!columnReorderEnabled}
                            >
                            {columns.map((col, colIndex) => {
                                const stickyHeader = shouldStickyDataColumn(colIndex);
                                const slot = getStickySlotForDataColumn(colIndex);
                                const mi = getStickyMeasureIndexForDataColumn(colIndex);
                                const stickyColClass =
                                    stickyHeader && slot >= 0
                                        ? slot === 0
                                            ? styles.stickyCol0
                                            : slot === 1
                                              ? styles.stickyCol1
                                              : styles.stickyCol2
                                        : "";
                                const headerStickyClass =
                                    stickyHeader && stickyColClass
                                        ? `${styles.stickyPinned} ${stickyColClass}${
                                              slot >= 0 && isLastStickySlot(slot) ? ` ${styles.stickyPinnedLast}` : ""
                                          }`
                                        : "";
                                const headerStickyStyle =
                                    stickyHeader && mi >= 0 ? { left: stickyColumnLefts[mi] } : undefined;
                                const headerCellClassName = `${styles["header-cell"]} ${headerStickyClass}`.trim();
                                const sortDirection =
                                    sortConfig.key === col.key ? sortConfig.direction : false;

                                const headerContent =
                                    enableColumnFilters && col.filterable !== false ? (
                                        <div className={styles["header-filter-wrapper"]}>
                                            {hasDropdownFilter(col.key) ? (
                                                <>
                                                    <TextField
                                                        ref={el => anchorRefs.current[col.key] = el}
                                                        size="small"
                                                        placeholder={col.label}
                                                        value={Array.isArray(columnFilters[col.key]) && columnFilters[col.key].length > 0
                                                            ? `${columnFilters[col.key].length} selected`
                                                            : ""}
                                                        readOnly
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!dropdownOpen[col.key]) {
                                                                handleDropdownToggle(col.key);
                                                            }
                                                        }}
                                                        onFocus={(e) => {
                                                            e.stopPropagation();
                                                            if (!dropdownOpen[col.key]) {
                                                                handleDropdownToggle(col.key);
                                                            }
                                                        }}
                                                        className={styles["column-filter-input"]}
                                                        style={{ width: 'auto', minWidth: `${col.label.length * 6 + 36}px`, cursor: 'pointer' }}
                                                        InputProps={{
                                                            disableUnderline: true,
                                                            readOnly: true,
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                        {Array.isArray(columnFilters[col.key]) && columnFilters[col.key].length > 0 && (
                                                                            <span className={styles["filter-count-badge"]}>
                                                                                {columnFilters[col.key].length}
                                                                            </span>
                                                                        )}
                                                                        {enableSorting && col.sortable !== false ? (
                                                                            <TableSortLabel
                                                                                active={sortConfig.key === col.key}
                                                                                direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleSort(col.key);
                                                                                }}
                                                                                className={styles["sort-label-inline"]}
                                                                                IconComponent={SortIconComponent}
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                    <Popper
                                                        open={dropdownOpen[col.key] || false}
                                                        anchorEl={anchorRefs.current[col.key]}
                                                        placement="bottom-start"
                                                        style={{ zIndex: 1300 }}
                                                        modifiers={[
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 4],
                                                                },
                                                            },
                                                        ]}
                                                    >
                                                        <ClickAwayListener 
                                                            onClickAway={(event) => {
                                                                // Don't close if clicking on the anchor element (TextField) or inside any dropdown
                                                                if (anchorRefs.current[col.key] && anchorRefs.current[col.key].contains(event.target)) {
                                                                    return;
                                                                }
                                                                // Check if clicking inside any other dropdown
                                                                const clickedInsideDropdown = Object.keys(anchorRefs.current).some(key => {
                                                                    const ref = anchorRefs.current[key];
                                                                    return ref && ref !== anchorRefs.current[col.key] && ref.contains(event.target);
                                                                });
                                                                if (clickedInsideDropdown) {
                                                                    return;
                                                                }
                                                                // Close all dropdowns
                                                                setDropdownOpen({});
                                                            }}
                                                        >
                                                            <Paper className={styles["dropdown-paper"]}>
                                                                <div className={styles["dropdown-header"]}>
                                                                    <TextField
                                                                        size="small"
                                                                        placeholder={`Search ${col.label}`}
                                                                        value={dropdownSearch[col.key] || ""}
                                                                        onChange={(e) => handleDropdownSearchChange(col.key, e.target.value)}
                                                                        className={styles["dropdown-search-input"]}
                                                                        inputRef={(el) => {
                                                                            dropdownSearchInputRefs.current[col.key] = el;
                                                                        }}
                                                                        InputProps={{
                                                                            startAdornment: (
                                                                                <InputAdornment position="start">
                                                                                    <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
                                                                                </InputAdornment>
                                                                            ),
                                                                            disableUnderline: true,
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                                <DropdownList
                                                                    columnKey={col.key}
                                                                    allValues={uniqueColumnValues[col.key] || []}
                                                                    searchText={dropdownSearch[col.key] || ''}
                                                                    selectedValues={Array.isArray(columnFilters[col.key]) ? columnFilters[col.key] : []}
                                                                    onToggle={handleDropdownValueToggle}
                                                                />
                                                            </Paper>
                                                        </ClickAwayListener>
                                                    </Popper>
                                                </>
                                            ) : (
                                            <TextField
                                                size="small"
                                                placeholder={col.label}
                                                value={columnFilters[col.key] || ""}
                                                onChange={(e) => handleColumnFilterChange(col.key, e.target.value)}
                                                className={styles["column-filter-input"]}
                                                style={{ width: 'auto', minWidth: `${col.label.length * 6 + 36}px` }}
                                                InputProps={{
                                                    disableUnderline: true,
                                                    endAdornment: enableSorting && col.sortable !== false ? (
                                                        <InputAdornment position="end">
                                                            <TableSortLabel
                                                                active={sortConfig.key === col.key}
                                                                direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSort(col.key);
                                                                }}
                                                                className={styles["sort-label-inline"]}
                                                                IconComponent={SortIconComponent}
                                                            />
                                                        </InputAdornment>
                                                    ) : null,
                                                }}
                                            />
                                            )}
                                        </div>
                                    ) : (
                                        <div className={styles["header-cell-content"]}>
                                            {enableSorting && col.sortable !== false ? (
                                                <TableSortLabel
                                                    active={sortConfig.key === col.key}
                                                    direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                                                    onClick={() => handleSort(col.key)}
                                                    className={styles["sort-label"]}
                                                    IconComponent={SortIconComponent}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            ) : (
                                                <span>{col.label}</span>
                                            )}
                                        </div>
                                    );

                                if (columnReorderEnabled) {
                                    return (
                                        <SortableHeaderCell
                                            key={col.key}
                                            id={col.key}
                                            label={col.label}
                                            className={headerCellClassName}
                                            style={headerStickyStyle}
                                            sortDirection={sortDirection}
                                        >
                                            {headerContent}
                                        </SortableHeaderCell>
                                    );
                                }

                                return (
                                <TableCell
                                    key={col.key}
                                    className={headerCellClassName}
                                    sortDirection={sortDirection}
                                    style={headerStickyStyle}
                                >
                                    {headerContent}
                                </TableCell>
                                );
                            })}
                            </SortableContext>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length === 0 ? (
                            <TableRow className={styles["empty-row"]}>
                                <TableCell
                                    colSpan={columns.length + (enableSelection ? 1 : 0)}
                                    className={styles["empty-cell"]}
                                >
                                    No data available
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((row) => {
                                const rowId = row[rowKey];
                                const isSelected = isRowSelected(rowId);
                                return (
                                    <TableRow
                                        key={rowId}
                                        className={`${styles["data-row"]} ${isSelected ? styles["selected-row"] : ""}`}
                                        hover
                                    >
                                        {enableSelection && (
                                            <TableCell
                                                className={`${styles["checkbox-cell"]} ${styles.stickyPinned} ${styles.stickyCol0}${
                                                    isLastStickySlot(0) ? ` ${styles.stickyPinnedLast}` : ""
                                                }`}
                                                padding="checkbox"
                                                style={{ left: stickyColumnLefts[0] }}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleSelectRow(rowId)}
                                                    className={styles["checkbox"]}
                                                    sx={{
                                                        '&.Mui-checked': {
                                                            color: 'rgba(79, 70, 229, 0.60)',
                                                        },
                                                        '&.Mui-checked .MuiSvgIcon-root': {
                                                            fill: 'rgba(79, 70, 229, 0.60)',
                                                            color: 'rgba(79, 70, 229, 0.60)',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((col, colIndex) => {
                                            const cellStyle = getCellStyle(col, row[col.key], row);
                                            const stickyBody = shouldStickyDataColumn(colIndex);
                                            const slot = getStickySlotForDataColumn(colIndex);
                                            const mi = getStickyMeasureIndexForDataColumn(colIndex);
                                            const stickyColClassBody =
                                                stickyBody && slot >= 0
                                                    ? slot === 0
                                                        ? styles.stickyCol0
                                                        : slot === 1
                                                          ? styles.stickyCol1
                                                          : styles.stickyCol2
                                                    : "";
                                            const bodyStickyClass =
                                                stickyBody && stickyColClassBody
                                                    ? `${styles.stickyPinned} ${stickyColClassBody}${
                                                          slot >= 0 && isLastStickySlot(slot)
                                                              ? ` ${styles.stickyPinnedLast}`
                                                              : ""
                                                      }`
                                                    : "";
                                            const bodyStickyStyle =
                                                stickyBody && mi >= 0
                                                    ? { ...cellStyle, left: stickyColumnLefts[mi] }
                                                    : cellStyle;
                                            return (
                                                <TableCell 
                                                    key={col.key} 
                                                    className={`${styles["data-cell"]} ${bodyStickyClass}`.trim()}
                                                    style={bodyStickyStyle}
                                                >
                                                    {renderCellValue(col, row)}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <DragOverlay dropAnimation={{ duration: 220, easing: "cubic-bezier(0.2, 0, 0, 1)" }}>
                {activeDragColumn ? (
                    <div className={styles["column-drag-overlay"]} role="presentation">
                        <DragIndicatorIcon className={styles["column-drag-overlay-icon"]} fontSize="small" />
                        <span className={styles["column-drag-overlay-label"]}>
                            {activeDragColumn.label || activeDragColumn.key}
                        </span>
                    </div>
                ) : null}
            </DragOverlay>
            </DndContext>
        </div>
    );
}

export default CustomTable;
